import { askAgent, streamAgent } from "@vida/oceany-agent-client";
import {
  newSpeakerNoteFromAnswer,
  sanitizeSpeakerNoteMarkdown,
  setSpeakerScript,
  slideContentSummary,
  truncateSpeakerNoteChars,
  type SurfDeck,
  type SurfSlide,
  type SurfSpeakerNote,
  type SurfSpeakerNoteKind,
} from "@vida/surf-core";
import { getIdToken } from "../auth";
import { APP_ID, getAppsyncEndpoint } from "../config";
import { createOceanSignedFetch } from "./oceanSignedFetch";
import {
  getOceanAgentStreamUrl,
  getSurfWhaleAgentId,
  isSpeakerWhaleConfigured,
} from "./surfOceanConfig";

export { isSpeakerWhaleConfigured } from "./surfOceanConfig";

import type { SpeakerWhaleMissionStep } from "./speakerWhaleStep.js";

export type SpeakerWhaleProgress = {
  kind: "meta" | "step" | "delta";
  label: string;
  agentName?: string;
  step?: SpeakerWhaleMissionStep;
};

export type SpeakerWhaleResult = {
  answer: string;
  jobId?: string;
  agentName: string;
};

export type SpeakerWhalePriorTurn = {
  question: string;
  answer: string;
  agentName: string;
};

const GOAL_MAX_CHARS = 12_000;

function slideTitle(slide: SurfSlide): string {
  return (
    slide.layers.find((l) => l.type === "text" && l.textRole === "title")?.content?.trim() ??
    "Slide"
  );
}

function clip(text: string, max: number): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

export function buildSpeakerWhaleGoal(
  question: string,
  deck: SurfDeck,
  slide: SurfSlide,
  priorTurns: SpeakerWhalePriorTurn[] = [],
): string {
  const trimmed = question.trim();
  if (!trimmed) return trimmed;

  const slideIndex = deck.slides.findIndex((s) => s.id === slide.id);
  const section = deck.outline?.find((s) => s.id === slide.outlineSectionId);
  const ctx = deck.deckContext;
  const parts: string[] = [];

  parts.push("[Surf speaker — contesto slide e presentazione]");
  parts.push(`deckId: ${deck.id}`);
  parts.push(`slideId: ${slide.id}`);
  if (slideIndex >= 0) {
    parts.push(`slidePosition: ${slideIndex + 1} di ${deck.slides.length}`);
  }
  parts.push(`deckTitle: ${deck.title?.trim() || "(senza titolo)"}`);
  if (ctx?.summary?.trim()) {
    parts.push(`deckSummary: ${clip(ctx.summary, 1200)}`);
  }
  if (ctx?.themes?.length) {
    parts.push(`themes: ${ctx.themes.join(", ")}`);
  }
  if (ctx?.keyEntities?.length) {
    parts.push(`keyEntities: ${ctx.keyEntities.join(", ")}`);
  }
  if (ctx?.sourceExcerpts?.length) {
    parts.push(`sourceExcerpts:\n${clip(ctx.sourceExcerpts.join("\n---\n"), 1500)}`);
  }
  if (deck.sourceContent?.trim()) {
    parts.push(`sourceContent:\n${clip(deck.sourceContent, 2000)}`);
  }

  parts.push("\n[Slide corrente]");
  parts.push(`slideTitle: ${slideTitle(slide)}`);
  if (slide.outlineRole) parts.push(`slideRole: ${slide.outlineRole}`);
  if (section?.title?.trim()) {
    parts.push(`outlineSectionTitle: ${section.title.trim()}`);
  }

  const content = slideContentSummary(slide, deck);
  if (content.trim()) {
    parts.push(`slideContent:\n${clip(content, 3000)}`);
  }
  if (section?.bullets?.length) {
    parts.push(`outlineBullets:\n${section.bullets.map((b) => `- ${b}`).join("\n")}`);
  }
  if (section?.notes?.trim()) {
    parts.push(`outlineNotes: ${clip(section.notes, 800)}`);
  }
  if (slide.speakerNotes?.trim()) {
    parts.push(`slideSpeakerNotes: ${clip(slide.speakerNotes, 800)}`);
  }

  const script = slide.speakerScript?.text?.trim();
  if (script) {
    parts.push(`speakerScript:\n${clip(script, 4000)}`);
  }

  if (priorTurns.length > 0) {
    parts.push("\n[Q&A precedenti in questa sessione]");
    for (const turn of priorTurns.slice(-4)) {
      parts.push(`User: ${turn.question}`);
      parts.push(`${turn.agentName}: ${clip(turn.answer, 1500)}`);
    }
  }

  parts.push("\n[Richiesta utente]");
  parts.push(trimmed);

  const full = parts.join("\n");
  if (full.length <= GOAL_MAX_CHARS) return full;
  return clip(full, GOAL_MAX_CHARS - trimmed.length - 30) + `\n\n[Richiesta utente]\n${trimmed}`;
}

function buildContextJson(deck: SurfDeck, slide: SurfSlide) {
  const section = deck.outline?.find((s) => s.id === slide.outlineSectionId);
  return {
    source: "surf",
    deckId: deck.id,
    slideId: slide.id,
    slideTitle: slideTitle(slide),
    slideScript: slide.speakerScript?.text ?? "",
    slideContent: slideContentSummary(slide, deck),
    bullets: section?.bullets ?? [],
    themes: deck.deckContext?.themes ?? [],
    deckSummary: deck.title,
  };
}

export async function askSpeakerWhale(
  deck: SurfDeck,
  slideId: string,
  question: string,
  onProgress?: (p: SpeakerWhaleProgress) => void,
  opts?: { priorTurns?: SpeakerWhalePriorTurn[] },
): Promise<SpeakerWhaleResult> {
  if (!isSpeakerWhaleConfigured()) {
    throw new Error(
      "Whale speaker non configurato (surf_whale_agent_id / ocean stream / Cognito).",
    );
  }
  const slide = deck.slides.find((s) => s.id === slideId);
  if (!slide) throw new Error("Slide non trovata.");

  const goal = buildSpeakerWhaleGoal(question, deck, slide, opts?.priorTurns ?? []);
  const contextJson = buildContextJson(deck, slide);
  const streamUrl = getOceanAgentStreamUrl();
  const agentId = getSurfWhaleAgentId();
  const jwt = await getIdToken();
  const fetchFn = await createOceanSignedFetch();

  let jobId = "";
  let outputText = "";
  let agentName = "Whale";

  try {
    for await (const event of streamAgent({
      streamUrl,
      jwt,
      appId: APP_ID,
      agentId,
      goal,
      contextJson,
      fetchFn,
    })) {
      if (event.type === "meta") {
        jobId = event.jobId;
        agentName = event.agentName?.trim() || agentName;
        onProgress?.({
          kind: "meta",
          label: `Missione ${event.jobId.slice(0, 8)}…`,
          agentName,
        });
      } else if (event.type === "step") {
        const stepAgent = event.peerName?.trim() || agentName;
        if (event.peerName?.trim()) {
          agentName = event.peerName.trim();
        }
        onProgress?.({
          kind: "step",
          label: event.summary?.trim() || event.kind,
          agentName: stepAgent,
          step: {
            seq: event.seq,
            kind: event.kind,
            summary: event.summary,
            peerName: event.peerName,
            agentName: stepAgent,
          },
        });
      } else if (event.type === "delta") {
        outputText += event.text;
      } else if (event.type === "done") {
        jobId = event.jobId || jobId;
        outputText = event.outputText?.trim() || outputText;
        agentName = event.agentName?.trim() || agentName;
      } else if (event.type === "error") {
        throw new Error(event.message || "Errore agente Whale.");
      }
    }
  } catch {
    const asyncResult = await askAgent({
      appsyncUrl: getAppsyncEndpoint(),
      jwt,
      appId: APP_ID,
      agentId,
      goal,
      contextJson,
    });
    jobId = asyncResult.jobId;
    outputText = asyncResult.outputText ?? "";
  }

  return {
    answer: outputText.trim() || "(Nessuna risposta testuale.)",
    jobId,
    agentName,
  };
}

export function materializeWhaleNote(
  question: string,
  result: SpeakerWhaleResult,
  kind: SurfSpeakerNoteKind = "freeform",
): SurfSpeakerNote {
  const sanitized = sanitizeSpeakerNoteMarkdown(result.answer);
  const body = truncateSpeakerNoteChars(sanitized);
  return newSpeakerNoteFromAnswer(question, result.answer, result.agentName, result.jobId, kind, {
    body,
    rawBody: sanitized,
    question: question.trim() || undefined,
    summaryStatus: "fallback",
    collapsed: true,
  });
}

export type WhaleScriptApplyMode = "replace" | "append" | "prepend";

export function applyWhaleAnswerToScript(
  deck: SurfDeck,
  slideId: string,
  answer: string,
  mode: WhaleScriptApplyMode,
): SurfDeck {
  const clean = sanitizeSpeakerNoteMarkdown(answer);
  const slide = deck.slides.find((s) => s.id === slideId);
  const prev = slide?.speakerScript?.text ?? "";
  const text =
    mode === "replace"
      ? clean
      : mode === "append"
        ? [prev, clean].filter(Boolean).join("\n\n")
        : [clean, prev].filter(Boolean).join("\n\n");
  return setSpeakerScript(deck, slideId, { text, status: "edited" });
}
